import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useNavigate, useParams } from 'react-router-dom';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Grid, Box, Tab, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import NotificationSettings from 'components/user-settings/notification-settings/notification-settings.component';
import SystemSettings from 'components/user-settings/system-settings/system-settings.component';
import ConstructionSettings from 'components/user-settings/construction-settings/construction-settings.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import AccountSettings from 'components/user-settings/account-settings/account-settings.component';

const UserSettings: FC = () => {
    const { tab } = useParams<{ tab: string }>();
    const currentUser = currentUserVar();
    const [tabState, setTabState] = useState(tab || '');
    const { t: translate } = useTranslation(['user-settings']);
    const navigate = useNavigate();

    const handleTabChange = (_event: unknown, newTabState: string) => {
        navigate(`/user-settings/${newTabState}`);
        setTabState(newTabState);
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('user-settings:USER_SETTINGS')} />
            <TabContext value={tabState}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                        <Paper variant="outlined" square>
                            <Box>
                                <TabList
                                    orientation="vertical"
                                    variant="scrollable"
                                    onChange={handleTabChange}
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <SettingsTab
                                        label={translate('user-settings:ACCOUNT_SETTINGS')}
                                        value="account-settings"
                                    />
                                    <SettingsTab
                                        label={translate('user-settings:SYSTEM_SETTINGS')}
                                        value="system-settings"
                                    />
                                    {permissions.isDentist(currentUser?.type) ? (
                                        <SettingsTab
                                            label={translate('user-settings:NOTIFICATIONS')}
                                            value="notification-settings"
                                        />
                                    ) : undefined}
                                    {permissions.isDentist(currentUser?.type) ? (
                                        <SettingsTab
                                            label={translate('user-settings:CONSTRUCTION_SETTINGS')}
                                            value="construction-settings"
                                        />
                                    ) : undefined}
                                </TabList>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TabPanel sx={{ padding: 0 }} value="account-settings">
                            <Paper>
                                <Box p={2}>
                                    <AccountSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="notification-settings">
                            <Paper>
                                <Box p={2}>
                                    <NotificationSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="system-settings">
                            <Paper>
                                <Box p={2}>
                                    <SystemSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                        <TabPanel sx={{ padding: 0 }} value="construction-settings">
                            <Paper>
                                <Box p={2}>
                                    <ConstructionSettings />
                                </Box>
                            </Paper>
                        </TabPanel>
                    </Grid>
                </Grid>
            </TabContext>
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        color: theme?.palette.primary.main,
        maxWidth: 'none',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.light,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
    };
});

export default UserSettings;
